import { checkGatewayHealth } from './gatewayHealth';
import { healthStateStore, GatewayHealthState } from './healthStateStore';
import { contextState } from '../../context/contextState';

type HealthEventHandler = (state: GatewayHealthState) => void;

class GatewayHealthServiceSingleton {
    private isRunning: boolean = false;
    private timeoutId: NodeJS.Timeout | null = null;
    private listeners: Record<string, HealthEventHandler[]> = {
        'gateway_online': [],
        'gateway_offline': []
    };

    /**
     * Start the background polling loop. Prevents duplicate loops.
     */
    start() {
        if (this.isRunning) {
            return;
        }
        this.isRunning = true;
        
        // 🥇 TIER 1 FORENSIC LOG
        console.log('[Gateway] ENV:', (import.meta as any).env.VITE_GATEWAY_URL);
        
        this.poll();
    }

    /**
     * Stop the background polling loop.
     */
    stop() {
        this.isRunning = false;
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    /**
     * Get the current gateway health state.
     */
    getState(): GatewayHealthState {
        return healthStateStore.getState();
    }

    /**
     * Get the dynamically discovered gateway URL.
     */
    getActiveGateway(): string | null {
        return (window as any).__WINGMAN_ACTIVE_GATEWAY || null;
    }

    /**
     * Subscribe to health change events.
     * Events: 'gateway_online', 'gateway_offline'
     */
    on(event: 'gateway_online' | 'gateway_offline', handler: HealthEventHandler) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(handler);
    }

    off(event: 'gateway_online' | 'gateway_offline', handler: HealthEventHandler) {
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(h => h !== handler);
        }
    }

    private emit(event: 'gateway_online' | 'gateway_offline', data: GatewayHealthState) {
        if (this.listeners[event]) {
            this.listeners[event].forEach(handler => handler(data));
        }
    }

    private async poll() {
        if (!this.isRunning) return;

        try {
            const isOnline = await checkGatewayHealth();
            const currentState = healthStateStore.getState();
            const now = Date.now();
            
            const wasOnline = currentState.online;
            const wasUnchecked = currentState.lastChecked === 0;

            if (isOnline) {
                healthStateStore.updateState({
                    online: true,
                    lastChecked: now,
                    failureCount: 0,
                    pollingInterval: 10000
                });
                if (!wasOnline || wasUnchecked) {
                    console.info(`%c[SmartBridge] SUCCESS: Backend discovered at ${(import.meta as any).env.VITE_GATEWAY_URL || 'Localhost'}`, 'color: #22c55e; font-weight: bold;');
                    contextState.setConnectionStatus('online');
                    this.emit('gateway_online', healthStateStore.getState());
                }
            } else {
                healthStateStore.updateState({
                    online: false,
                    lastChecked: now,
                    failureCount: currentState.failureCount + 1,
                    pollingInterval: 5000
                });
                if (wasOnline || wasUnchecked) {
                    console.warn('%c[SmartBridge] BACKEND OFFLINE', 'color: #ef4444; font-weight: bold;');
                    contextState.setConnectionStatus('offline');
                    this.emit('gateway_offline', healthStateStore.getState());
                }
            }
        } catch (error) {
            console.debug("[GatewayHealthService] Unexpected error:", error);
        }

        if (this.isRunning) {
            const nextInterval = healthStateStore.getState().pollingInterval;
            this.timeoutId = setTimeout(() => this.poll(), nextInterval);
        }
    }
}

export const GatewayHealthService = new GatewayHealthServiceSingleton();